/* RESPONSIBLE TEAM: team-standalone */
import Service, { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import RSVP from 'rsvp';
import type FreshdeskConfiguration from 'embercom/models/standalone/freshdesk-configuration';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { get, put } from 'embercom/lib/ajax';
import type ContentImportService from './content-import-service';
import { type TaskGenerator } from 'ember-concurrency';
import type RouterService from '@ember/routing/router-service';
import type IntercomMessengerConfiguration from 'embercom/models/standalone/intercom-messenger-configuration';
import type KnowledgeHubService from './knowledge-hub-service';
import type WorkflowConnectorInsertableActionsService from './workflow-connector-insertable-actions';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';

import { type KnowledgeSourceSummary } from 'embercom/objects/standalone/knowledge';
import { type FreshdeskTicketsSetupData } from 'embercom/objects/standalone/freshdesk';
import type ApplicationInstance from '@ember/application/instance';
import type Integration from 'embercom/models/standalone/integration';
import { IntegrationType } from 'embercom/objects/standalone/constants';

import type Platform from 'embercom/objects/standalone/platforms/platform';
import ZendeskPlatform from 'embercom/objects/standalone/platforms/zendesk';
import SalesforcePlatform from 'embercom/objects/standalone/platforms/salesforce';
import FreshdeskPlatform from 'embercom/objects/standalone/platforms/freshdesk';
import HubspotPlatform from 'embercom/objects/standalone/platforms/hubspot';
import CustomHelpdeskPlatform from 'embercom/objects/standalone/platforms/custom-helpdesk';
import GenericPlatform from 'embercom/objects/standalone/platforms/generic';
import { getOwner } from '@ember/application';
import { dependentKeyCompat } from '@ember/object/compat';
import type ZendeskConfiguration from 'embercom/models/standalone/zendesk-configuration';
import ENV from 'embercom/config/environment';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type ImportSource from 'embercom/models/import-service/import-source';
import { type default as PredicateGroup } from 'predicates/models/predicate-group';
import type Session from 'embercom/services/session';
import type Workspace from 'embercom/objects/workspace';

export default class FinStandaloneService extends Service {
  @service declare store: Store;
  @service declare appService: { app: $TSFixMe };
  @service declare session: Session;
  @service declare notificationsService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare contentImportService: ContentImportService;
  @service declare router: RouterService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;

  @tracked intercomMessengerConfig!: IntercomMessengerConfiguration;
  @tracked messengerSettings!: $TSFixMe;
  @tracked operatorIdentity?: $TSFixMe;
  @tracked operatorIdentityPreviewImgSrc = '';
  @tracked operatorIdentityPreviewDarkImgSrc = '';

  /* Freshdesk specific */
  @tracked freshdeskConfig?: FreshdeskConfiguration;
  @tracked freshdeskTicketsSetupData: FreshdeskTicketsSetupData = {
    fin_user_id: undefined,
    all_possible_fin_users: [],
  };

  @tracked isConversationSyncingEnabled: boolean | undefined = undefined;
  @tracked standaloneImportSource?: ImportSource;
  @tracked standaloneIntegrations: Integration[] = [];

  @tracked private _zendeskPlatform!: ZendeskPlatform;
  @tracked private _salesforcePlatform!: SalesforcePlatform;
  @tracked private _hubspotPlatform!: HubspotPlatform;
  @tracked private _freshdeskPlatform!: FreshdeskPlatform;
  @tracked private _customHelpdeskPlatform!: CustomHelpdeskPlatform;

  get zendeskPlatform() {
    if (!this.app.hasStandalonePlatform('zendesk') && ENV.environment === 'production') {
      // eslint-disable-next-line no-console
      console.trace(
        'Attempted to access finStandaloneService.zendeskConfig but the workspace does not have the Zendesk platform',
      );
      return this._zendeskPlatform;
    } else if (this.app.hasStandalonePlatform('zendesk')) {
      return this._zendeskPlatform;
    } else {
      throw new Error(
        'Attempted to access finStandaloneService.zendeskPlatform but the workspace does not have the Zendesk platform',
      );
    }
  }

  get salesforcePlatform() {
    if (!this.app.hasStandalonePlatform('salesforce') && ENV.environment === 'production') {
      // eslint-disable-next-line no-console
      console.trace(
        'Attempted to access finStandaloneService.salesforcePlatform but the workspace does not have the Salesforce platform',
      );
      return this._salesforcePlatform;
    } else if (this.app.hasStandalonePlatform('salesforce')) {
      return this._salesforcePlatform;
    } else {
      throw new Error(
        'Attempted to access finStandaloneService.salesforcePlatform but the workspace does not have the Salesforce platform',
      );
    }
  }

  // TODO: SA Generic platform migration – Remove this indirection
  get zendeskConfig(): ZendeskConfiguration | undefined {
    return this.zendeskPlatform.zendeskConfig;
  }

  // TODO: SA Generic platform migration – Remove this indirection
  get salesforceConfig(): SalesforceConfiguration | undefined {
    return this.salesforcePlatform.salesforceConfig;
  }

  constructor(owner: ApplicationInstance) {
    super(owner);

    // TODO: SA Generic platform migration – When all data loading is moved to the platform, we can get rid of these
    // platform specific instances and convert `platform` to be a tracked property instead of a getter, and replace
    // the current getter logic with a `setupPlatform` method.
    // If we attempt to setup a ZendeskPlatform in the getter we will lose any data a previous instance has loaded.
    this._salesforcePlatform = new SalesforcePlatform(owner, {});
    this._zendeskPlatform = new ZendeskPlatform(owner, {});
    this._hubspotPlatform = new HubspotPlatform(owner);
    this._freshdeskPlatform = new FreshdeskPlatform(owner);
    this._customHelpdeskPlatform = new CustomHelpdeskPlatform(owner, {});
  }

  get app() {
    return this.appService.app;
  }

  get workspace(): Workspace {
    return this.session.workspace;
  }

  hasStandalonePlatform(platform: string) {
    if (this.app) {
      return this.app.hasStandalonePlatform(platform);
    } else if (this.workspace) {
      return this.workspace.hasStandalonePlatform(platform);
    } else {
      console.error('No workspace or app found in Fin Standalone Service');
      return false;
    }
  }

  @dependentKeyCompat
  get platform(): Platform {
    // note that this function currently does not require initialization of the service
    // there are places where we call this on suite pages
    let owner = getOwner(this) as ApplicationInstance;

    if (this.hasStandalonePlatform('zendesk')) {
      return this.zendeskPlatform;
    } else if (this.hasStandalonePlatform('salesforce')) {
      return this._salesforcePlatform;
    } else if (this.hasStandalonePlatform('freshdesk')) {
      return this._freshdeskPlatform;
    } else if (this.hasStandalonePlatform('hubspot')) {
      return this._hubspotPlatform;
    } else if (this.hasStandalonePlatform('custom_helpdesk')) {
      return this._customHelpdeskPlatform;
    } else {
      return new GenericPlatform(owner, {
        intercomMessengerConfig: this.intercomMessengerConfig,
      });
    }
  }

  get workflowsReturnRoute(): string {
    let current = this.router.currentRouteName;

    if (current === 'apps.app.standalone.channels.zendesk.tickets.workflow') {
      return 'apps.app.standalone.deploy.zendesk.tickets.workflow.show';
    }

    if (current === 'apps.app.standalone.channels.zendesk.messaging.workflow') {
      return 'apps.app.standalone.deploy.zendesk.messaging.workflow.show';
    }

    if (current === 'apps.app.standalone.channels.intercom.messenger.workflow') {
      return 'apps.app.standalone.deploy.intercom-messenger.integrate';
    }

    if (current === 'apps.app.standalone.tasks.workflow') {
      return 'apps.app.standalone.tasks';
    }

    console.error('No return route defined for: ', current);

    return 'apps.app.standalone.setup';
  }

  get hasContentForFin() {
    return Object.keys(this.knowledgeSourceSummary).some((key) => {
      return this.knowledgeSourceSummary[Number(key)].used_by_fin_count > 0;
    });
  }

  get initialized() {
    return (
      taskFor(this.initializationTask)?.isRunning ||
      taskFor(this.initializationTask)?.performCount > 0
    );
  }

  async initialize() {
    if (!this.app.canUseStandalone) {
      return;
    }

    let initializationTaskInstance = taskFor(this.initializationTask).last;

    if (!initializationTaskInstance) {
      await taskFor(this.initializationTask).perform();
    } else if (initializationTaskInstance.isRunning) {
      await initializationTaskInstance;
    }
  }

  @dropTask *initializationTask() {
    let promises: Promise<unknown>[] = [
      taskFor(this.loadIntercomMessengerConfig).perform(),
      this.store.findAll('conversation-attributes/descriptor'),
      this.workflowConnectorInsertableActions.loadActions(),
      taskFor(this.peekOrLoadMessengerSettings).perform(),
    ];

    if (this.app.canUseFeature('team-standalone-fin-over-api')) {
      promises.push(this.store.findRecord('standalone/api-configuration', this.app.id));
    }

    if (this.app.canUseFeature('fin-agent-api')) {
      promises.push(this.store.queryRecord('fin-agent-api/settings', { app_id: this.app.id }));
    }

    promises.push(taskFor(this.loadStandaloneIntegrations).perform());
    promises.push(this.platform.loadRequiredData());

    // TODO: SA Generic platform migration – Keep only platform calls

    promises.push(this.fetchStandaloneImportSource());

    if (this.app.hasStandalonePlatform('freshdesk')) {
      promises.push(taskFor(this.loadFreshdeskConfig).perform());
    }

    this.contentImportService.fetchContentImportSources();
    this.knowledgeHubService.fetchKnowledgeUsageSummary();

    try {
      yield RSVP.all(promises);

      // Salesforce lazy loads are handled by the platform

      // We need freshdesk config loaded to efficiently load the setup data
      if (this.app.hasStandalonePlatform('freshdesk')) {
        yield taskFor(this.loadFreshdeskTicketsSetupData).perform();
      }
    } catch (error) {
      console.error(error);
    }

    // this call is here to populate the contentLibrarySummary for Fin step in workflows
    taskFor(this.loadAiContentLibrarySummary).perform();
    taskFor(this.loadOperatorIdentity).perform();
  }

  async loadBasePlatformConfigs() {
    if (!this.app.canUseStandalone) {
      return;
    }

    if (this.initialized) {
      return;
    }

    this.platform.loadConfigData();

    // Salesforce config is loaded via the platform
  }

  @dropTask *loadFreshdeskConfig() {
    try {
      this.freshdeskConfig = yield this.store.findRecord(
        'standalone/freshdesk-configuration',
        this.app.id,
      );

      if (!this.freshdeskConfig) {
        this.freshdeskConfig = this.store.createRecord('standalone/freshdesk-configuration', {
          id: this.app.id,
        });
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadFreshdeskTicketsSetupData(): TaskGenerator<void> {
    if (this.freshdeskConfig?.isPendingSetupForTickets) {
      this.freshdeskTicketsSetupData = {
        fin_user_id: undefined,
        all_possible_fin_users: [],
      };

      return;
    }

    let freshdeskIntegrationId = this.standaloneIntegrations.find(
      (integration) => integration.integrationType === IntegrationType.FreshdeskTickets,
    )?.integrationId;

    if (!freshdeskIntegrationId) {
      return;
    }

    try {
      let setupData = yield get(
        `/ember/standalone/integrations/${IntegrationType.FreshdeskTickets}/${freshdeskIntegrationId}/fin_user_identity`,
        {
          app_id: this.app.id,
        },
      );

      this.freshdeskTicketsSetupData = {
        fin_user_id: setupData.fin_user_id,
        all_possible_fin_users: setupData.all_possible_fin_users || [],
      };
    } catch (e) {
      console.error(e.message || e.jqXHR);
      // Set empty data on error
      this.freshdeskTicketsSetupData = {
        fin_user_id: undefined,
        all_possible_fin_users: [],
      };
    }
  }

  @dropTask *loadIntercomMessengerConfig(): TaskGenerator<void> {
    try {
      this.intercomMessengerConfig = yield this.store.findRecord(
        'standalone/intercom-messenger-configuration',
        this.app.id,
      );
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadStandaloneIntegrations(): TaskGenerator<void> {
    let integrations = yield this.store.findAll('standalone/integration');
    this.standaloneIntegrations = integrations;
  }

  get knowledgeSourceSummary(): KnowledgeSourceSummary {
    let knowledgeHubUsageSummary = this.knowledgeHubService.usageSummary || {};

    return Object.entries(knowledgeHubUsageSummary).reduce((summary, [key, value]) => {
      summary[Number(key)] = {
        entity_type: Number(key),
        total_count: value.all,
        used_by_fin_count: value.agent,
        additional_data: undefined,
      };
      return summary;
    }, {} as KnowledgeSourceSummary);
  }

  @restartableTask
  *loadOperatorIdentity(): TaskGenerator<void> {
    try {
      this.operatorIdentity = yield this.store.findRecord('operator-identity', '1');
      // operator identity can be modified, but not saved, in settings
      // and it looks strange to have a partially edited record when we visit this page
      this.operatorIdentity.rollbackAttributes();
      this.operatorIdentityPreviewImgSrc = this.operatorIdentity.avatar?.image_urls?.square_128;
      this.operatorIdentityPreviewDarkImgSrc =
        this.operatorIdentity.avatarDark?.image_urls?.square_128;
    } catch (e) {
      this.notificationsService.notifyError(this.intl.t('operator.settings.error'));
    }
  }

  get isLoadingOperatorIdentity() {
    return taskFor(this.loadOperatorIdentity).isRunning;
  }

  @action async updateOperatorIdentity() {
    await taskFor(this.saveOperatorIdentity).perform();
  }

  @restartableTask
  *saveOperatorIdentity(): TaskGenerator<void> {
    try {
      yield this.permissionsService.checkPermission('can_create_and_edit_bots');
    } catch (e) {
      return;
    }

    try {
      if (this.app.canUseFeature('bot_identity')) {
        yield this.operatorIdentity.save();
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.settings.saved-successfully'),
      );
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t('operator.settings.error'));
    }
  }

  get isSavingOperatorIdentity() {
    return taskFor(this.saveOperatorIdentity).isRunning;
  }

  @action
  setOperatorIdentityPreviewImgSrc(url: string) {
    this.operatorIdentityPreviewImgSrc = url;
  }

  @action
  setOperatorIdentityPreviewDarkImgSrc(url: string) {
    this.operatorIdentityPreviewDarkImgSrc = url;
  }

  @action async saveFreshdeskConfig() {
    if (!this.freshdeskConfig) {
      throw new Error('freshdeskConfig is not present.');
    }

    if (this.freshdeskConfig) {
      this.freshdeskConfig.subdomain = this.freshdeskConfig.subdomain?.trim();
      this.freshdeskConfig.apiKey = this.freshdeskConfig.apiKey?.trim();
    }

    try {
      await this.freshdeskConfig.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.setup.api.save-success'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.setup.api.save-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
      throw e;
    }
  }

  async fetchStandaloneImportSource(): Promise<void> {
    let response = await get('/ember/import_service/import_sources/standalone_sync_import_source', {
      app_id: this.app.id,
    });
    if (response.id) {
      let standaloneImportSource = await this.store.findRecord(
        'import-service/import-source',
        response.id,
        { reload: true },
      );

      this.isConversationSyncingEnabled = standaloneImportSource.isConversationSyncingEnabled;
      this.standaloneImportSource = standaloneImportSource;
    } else {
      // we'll create a new import source in paused state
      this.isConversationSyncingEnabled = false;
      await this.updateConversationSyncing(true);
    }
  }

  async updateConversationSyncing(
    updateSyncHistory = true,
    predicateGroup?: PredicateGroup,
    importMode?: string,
  ): Promise<void> {
    let payload: {
      app_id: string;
      sync_history?: boolean | undefined;
      predicate_group?: PredicateGroup;
      import_mode?: string;
    } = {
      app_id: this.app.id,
      import_mode: importMode,
    };

    if (updateSyncHistory) {
      payload.sync_history = this.isConversationSyncingEnabled;
    }

    if (predicateGroup) {
      payload.predicate_group = predicateGroup;
    }

    return await put(
      '/ember/import_service/import_sources/update_standalone_conversation_syncing',
      payload,
    );
  }

  get isLoadingFreshdeskConfig() {
    return taskFor(this.loadFreshdeskConfig).isRunning;
  }

  get isLoadingFreshdeskTicketsSetupData() {
    return taskFor(this.loadFreshdeskTicketsSetupData).isRunning;
  }

  @dropTask *loadAiContentLibrarySummary() {
    yield this.contentImportService.fetchAiContentLibrarySummary();
  }

  get isLoadingAiContentLibrarySummary() {
    return taskFor(this.loadAiContentLibrarySummary).isRunning;
  }

  get isFinAgentApiLive(): boolean {
    let finAgentApiSettings = this.store.peekRecord('fin-agent-api/settings', this.app.id);
    return finAgentApiSettings?.isInstalled ?? false;
  }

  @dropTask *peekOrLoadMessengerSettings(): TaskGenerator<void> {
    this.messengerSettings =
      this.store.peekRecord('messenger-settings/all', this.app.id) ||
      (yield this.store.findRecord('messenger-settings/all', this.app.id));
  }

  // Test helpers for stubbing certain standalone platform configs while migrating data loading to the platform objects
  // Designed to work with both the service level config objects and the platform level config objects
  set zendeskConfigForTest(zendeskConfig: ZendeskConfiguration) {
    if (ENV.environment !== 'test') {
      throw new Error('zendeskConfigForTest is only available in test environment');
    }

    this.zendeskPlatform.zendeskConfig = zendeskConfig;
  }

  set intercomMessengerConfigForTest(intercomMessengerConfig: IntercomMessengerConfiguration) {
    if (ENV.environment !== 'test') {
      throw new Error('intercomMessengerConfigForTest is only available in test environment');
    }

    this.intercomMessengerConfig = intercomMessengerConfig;
    this.platform.intercomMessengerConfig = intercomMessengerConfig;
  }
}
